import {
  AlertTriangle,
  ArrowRight,
  CreditCard,
  HandCoins,
  HelpCircle,
  LogOut,
  MapPin,
  Phone,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useCurrentReseller } from '../../api/reseller';
import { useActiveBrand } from '../../api/brand';

/**
 * POS-side suspended page — §8.2
 * Mounted by Laravel POS when the session detects the subscription is on hold.
 * Reseller-branded.
 */
export function PosSuspended() {
  const { data: r } = useCurrentReseller();
  const brand = useActiveBrand();
  if (!r) return null;
  const accent = r.primaryColor;

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-xl">
        {/* Brand */}
        <div className="flex flex-col items-center mb-7">
          <span
            className="w-14 h-14 rounded-2xl grid place-items-center text-white font-semibold text-xl shadow-sm"
            style={{ backgroundColor: accent }}
            aria-hidden="true"
          >
            {r.displayName.charAt(0)}
          </span>
          <p className="mt-3 text-sm text-zinc-500 dark:text-zinc-400">
            {r.displayName}
          </p>
        </div>

        {/* Message */}
        <div className="text-center mb-8">
          <span className="inline-flex w-12 h-12 grid place-items-center rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 mb-4">
            <AlertTriangle className="w-6 h-6" />
          </span>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Your POS is on hold
          </h1>
          <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-300 max-w-md mx-auto">
            Your subscription expired on{' '}
            <span className="font-medium text-zinc-800 dark:text-zinc-100 tabular-nums">
              02 May 2026
            </span>
            . Pay now to restore access in under a minute. Your data is safe.
          </p>
        </div>

        {/* Actions card */}
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-950 overflow-hidden">
          <a
            href={`https://saas.${brand.domain}/suspended`}
            className="flex items-center gap-4 px-5 py-4 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition border-b border-zinc-200 dark:border-zinc-700"
          >
            <span
              className="w-10 h-10 rounded-md grid place-items-center text-white flex-shrink-0"
              style={{ backgroundColor: accent }}
            >
              <CreditCard className="w-5 h-5" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-zinc-950 dark:text-zinc-50">
                Renew online via SSLCommerz
              </div>
              <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                Card · bKash · Nagad · bank — instant restore
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-zinc-400 flex-shrink-0" />
          </a>

          <div className="flex items-center gap-4 px-5 py-4">
            <span className="w-10 h-10 rounded-md grid place-items-center bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 flex-shrink-0">
              <HandCoins className="w-5 h-5" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-zinc-950 dark:text-zinc-50">
                Pay cash at office
              </div>
              <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  Sylhet, Bangladesh
                </span>
                <a
                  href={`tel:${r.supportPhone}`}
                  className="inline-flex items-center gap-1 mono hover:text-zinc-800 dark:hover:text-zinc-100"
                >
                  <Phone className="w-3 h-3" />
                  {r.supportPhone}
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 flex items-center justify-between gap-3 text-xs text-zinc-500 dark:text-zinc-400 flex-wrap">
          <span>Your subscription is on hold.</span>
          <div className="flex items-center gap-3">
            <a
              href={`mailto:${r.supportEmail}`}
              className="inline-flex items-center gap-1 hover:text-zinc-700 dark:hover:text-zinc-200"
            >
              <HelpCircle className="w-3.5 h-3.5" />
              Need help?
            </a>
            <span aria-hidden="true">·</span>
            <Link
              to="/login"
              className="inline-flex items-center gap-1 hover:text-zinc-700 dark:hover:text-zinc-200"
            >
              <LogOut className="w-3.5 h-3.5" />
              Log out
            </Link>
          </div>
        </div>

        <p className="mt-6 text-center text-[11px] text-zinc-400 dark:text-zinc-500">
          Powered by{' '}
          <span className="font-semibold text-zinc-600 dark:text-zinc-400">{r.displayName}</span>
        </p>
      </div>
    </div>
  );
}
